import { useCallback, useEffect, useState, useRef } from 'react';

import { searchIdentities, IdentityResponse } from '@api/v4/private/people_tagging/faces';
import useDebounce from '@components/common/custom_hooks/useDebounce';
import { Person } from '@components/people_tags/customPeopleTagTypes';

export enum IdentitySearchReturnFormat {
  IDENTITY = 'identity',
  PERSON = 'person'
}

// Mock data for testing People Filter
const MOCK_IDENTITIES_RESPONSE: IdentityResponse[] = [
  {
    id: '019d1988-7a4c-768f-9f23-9b8b3c52d243',
    name: 'Harvey Specter',
    face_count: 15,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/bj4ck8br3mph8th4b46j5n9v/v/114/faces/harvey-face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/bj4ck8br3mph8th4b46j5n9v/v/114/faces/' +
      '019d1988-76d5-768f-9f54-eac86a64ffbd/thumbnail.jpg?Expires=1774435776&KeyName=gcs-bfldr-dev' +
      '&Signature=Uf9EVg4WdtbinV0f2oKKdkijiIE='
  },
  {
    id: '019d1988-393e-768f-acb9-e824b04c485c',
    name: 'Mike Ross',
    face_count: 6,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/3srnv8qb5hw272pcxmsk8/v/111/faces/mike-face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/3srnv8qb5hw272pcxmsk8/v/111/faces/' +
      '019d1988-3257-768f-9256-833f64c97b8d/thumbnail.jpg?Expires=1774422256&KeyName=gcs-bfldr-dev' +
      '&Signature=Z9Lsgn-QHvmljBYQPq6pe0JxEvg='
  },
  {
    id: '019d1988-8b2d-768f-a1c7-d9f3e5a7b8c9',
    name: 'Donna Paulsen',
    face_count: 12,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/donna/face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/donna/thumbnail.jpg'
  },
  {
    id: '019d1988-9c3e-768f-b2d8-e0a4f6b9c0d1',
    name: 'Louis Litt', 
    face_count: 8,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/louis/face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/louis/thumbnail.jpg'
  },
  {
    id: '019d1988-ad4f-768f-c3e9-f1b5g7c0d2e3',
    name: 'Jessica Pearson',
    face_count: 18,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/jessica/face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/jessica/thumbnail.jpg'
  },
  {
    id: '019d1988-be5g-768f-d4f0-g2c6h8d1e3f4',
    name: 'Rachel Zane',
    face_count: 9,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/rachel/face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/rachel/thumbnail.jpg'
  },
  {
    id: '019d1988-cf6h-768f-e5g1-h3d7i9e2f4g5',
    name: 'Katrina Bennett',
    face_count: 4,
    face_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/katrina/face.jpg',
    identity_thumbnail_url: 'https://storage-us-dev-gcs.bfldr.com/example/katrina/thumbnail.jpg'
  }
];

interface UseIdentitySearchOptions {
  searchTerm: string;
  isDropdownOpen: boolean;
  limit?: number;
  excludeIdentityId?: string;
  includeLoading?: boolean;
  returnFormat?: IdentitySearchReturnFormat;
  useMockData?: boolean; // For testing purposes
}

export interface UseIdentitySearchReturn<T = IdentityResponse | Person> {
  results: T[];
  isLoading?: boolean;
  error: string | null;
}

export const useIdentitySearch = <T = IdentityResponse>({
  searchTerm,
  isDropdownOpen,
  limit = 50,
  excludeIdentityId,
  includeLoading = true,
  returnFormat = IdentitySearchReturnFormat.IDENTITY,
  useMockData = false
}: UseIdentitySearchOptions): UseIdentitySearchReturn<T> => {
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchDone = useRef(false);

  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    delay: 600
  });

  const fetchResults = useCallback(async (term: string, forceLoad = false) => {
    // Allow fetching initial data even when dropdown is closed
    if (!isDropdownOpen && !forceLoad) {
      setResults([]);
      if (includeLoading) setIsLoading(false);
      return;
    }

    if (includeLoading) setIsLoading(true);
    setError(null);

    try {
      let identities: IdentityResponse[];
      
      if (useMockData) {
        // Use mock data for testing
        await new Promise((resolve) => setTimeout(resolve, 300)); // Simulate API delay
        
        // Filter mock data based on search term
        identities = MOCK_IDENTITIES_RESPONSE.filter((identity) => {
          if (!term.trim()) return true; // Return all if no search term
          return identity.name.toLowerCase().includes(term.toLowerCase());
        }).slice(0, limit); // Apply limit
      } else {
        // Use real API
        const brandfolderKey = BFG.brandfolder_key;
        if (!brandfolderKey) {
          throw new Error('Brandfolder key not found');
        }

        identities = await searchIdentities({
          brandfolderKey,
          search: term || '',
          limit
        });
      }

      // Filter out excluded identity if specified
      if (excludeIdentityId) {
        identities = identities.filter((identity) => identity.id !== excludeIdentityId);
      }

      // Transform data based on return format
      let transformedResults: T[];

      if (returnFormat === IdentitySearchReturnFormat.PERSON) {
        transformedResults = identities.map((identity): Person => ({
          person_id: identity.id,
          tag_name: identity.name,
          face_uri: identity.identity_thumbnail_url || '',
          face_count: identity.face_count || 0
        })) as T[];
      } else {
        transformedResults = identities as T[];
      }

      setResults(transformedResults);
    } catch (err) {
      console.error('Error fetching identities:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch identities');
      setResults([]);
    } finally {
      if (includeLoading) setIsLoading(false);
    }
  }, [isDropdownOpen, limit, excludeIdentityId, returnFormat, includeLoading, useMockData]);

  // Fetch initial data on mount (first 50 identities)
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchResults('', true); // Empty search = get initial identities, force load
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Fetch filtered results only when user types (debounced search term changes)
  useEffect(() => {
    if (isDropdownOpen && debouncedSearchTerm.trim()) {
      fetchResults(debouncedSearchTerm, false);
    }
  }, [debouncedSearchTerm, isDropdownOpen, fetchResults]);

  const returnValue: UseIdentitySearchReturn<T> = {
    results,
    error
  };

  if (includeLoading) {
    returnValue.isLoading = isLoading;
  }

  return returnValue;
};
